import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Search, AppWindow, Globe } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useApplications } from "@/hooks/useApplications";
import { Application } from "@/types/application";
import { AppIcon } from "@/components/applications/AppIcon";
import { cn } from "@/lib/utils";
import {
  validateAppName,
  validateBundleId,
  WIZARD_VALIDATION_MESSAGES,
} from "@/services/wizardValidation";

export type AppConfigTargetMode = "catalog" | "external";

export interface AppConfigTargetValue {
  mode: AppConfigTargetMode;
  catalogAppId?: string;
  appName: string;
  bundleId: string;
}

export interface AppConfigTargetErrors {
  catalogAppId?: string;
  appName?: string;
  bundleId?: string;
}

interface AppConfigTargetStepProps {
  value: AppConfigTargetValue;
  onChange: (next: AppConfigTargetValue) => void;
  errors?: AppConfigTargetErrors;
  /** When true, the wizard was opened with a fixed target — disable changes. */
  locked?: boolean;
  /** Bundle IDs that already have at least one config — surfaced as a hint. */
  existingBundleIds?: Set<string>;
}



export const AppConfigTargetStep = ({
  value,
  onChange,
  errors,
  locked = false,
  existingBundleIds,
}: AppConfigTargetStepProps) => {
  const { applications, loading } = useApplications();
  const [search, setSearch] = useState("");

  // App Configurations are managed-app payloads — applicable to apps that
  // ship a Bundle ID. We don't filter by platform because managed app config
  // works on both iOS and macOS.
  const apps = useMemo(
    () => applications.filter((a) => !!a.bundleId),
    [applications],
  );

  const filteredApps = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        (a.bundleId ?? "").toLowerCase().includes(q),
    );
  }, [apps, search]);

  const selectCatalogApp = (app: Application) => {
    onChange({
      mode: "catalog",
      catalogAppId: app.id,
      appName: app.name,
      bundleId: app.bundleId ?? "",
    });
  };

  const setMode = (mode: AppConfigTargetMode) => {
    if (mode === "catalog") {
      onChange({
        mode,
        catalogAppId: undefined,
        appName: "",
        bundleId: "",
      });
    } else {
      onChange({
        mode,
        catalogAppId: undefined,
        appName: value.appName,
        bundleId: value.bundleId,
      });
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Target App</h2>
        <p className="text-sm text-muted-foreground mt-1">
          App Configuration policies push key/value settings to a managed app
          via MDM. Pick one from your catalog or describe an app installed
          outside Smplify.
        </p>
      </div>

      <RadioGroup
        value={value.mode}
        onValueChange={(v) => !locked && setMode(v as AppConfigTargetMode)}
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
        disabled={locked}
      >
        <Label
          htmlFor="appcfg-mode-catalog"
          className={cn(
            "flex items-start gap-3 border rounded-lg p-4 cursor-pointer transition-colors",
            value.mode === "catalog" && "border-primary bg-primary/5",
            locked && "opacity-60 cursor-not-allowed",
          )}
        >
          <RadioGroupItem
            id="appcfg-mode-catalog"
            value="catalog"
            className="mt-1"
          />
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <AppWindow className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">Pick from my catalog</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Bundle ID is auto-filled from the app's metadata.
            </p>
          </div>
        </Label>

        <Label
          htmlFor="appcfg-mode-external"
          className={cn(
            "flex items-start gap-3 border rounded-lg p-4 cursor-pointer transition-colors",
            value.mode === "external" && "border-primary bg-primary/5",
            locked && "opacity-60 cursor-not-allowed",
          )}
        >
          <RadioGroupItem
            id="appcfg-mode-external"
            value="external"
            className="mt-1"
          />
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">External app</span>
            </div>
            <p className="text-xs text-muted-foreground">
              For apps installed manually or via another tool.
            </p>
          </div>
        </Label>
      </RadioGroup>

      {value.mode === "catalog" && (
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search apps in your catalog…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                disabled={locked}
              />
            </div>

            {errors?.catalogAppId && (
              <p className="text-xs text-destructive">{errors.catalogAppId}</p>
            )}

            <ScrollArea className="h-[320px] pr-2">
              {loading ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  Loading apps…
                </p>
              ) : filteredApps.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No apps found in your catalog.
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredApps.map((app) => {
                    const selected = value.catalogAppId === app.id;
                    const alreadyConfigured =
                      !!app.bundleId &&
                      existingBundleIds?.has(app.bundleId);
                    return (
                      <button
                        key={app.id}
                        type="button"
                        onClick={() => !locked && selectCatalogApp(app)}
                        disabled={locked}
                        className={cn(
                          "w-full text-left flex items-center gap-3 p-3 rounded-md border transition-colors",
                          selected
                            ? "border-primary bg-primary/5"
                            : "hover:border-primary/40",
                        )}
                      >
                        <AppIcon
                          iconSrc={app.icon}
                          appName={app.name}
                          className="h-9 w-9"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">
                            {app.name}
                          </p>
                          <p className="text-xs font-mono text-muted-foreground truncate">
                            {app.bundleId}
                          </p>
                        </div>
                        {selected ? (
                          <Badge variant="secondary">Selected</Badge>
                        ) : alreadyConfigured ? (
                          <span className="text-xs text-muted-foreground shrink-0">
                            already configured
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {value.mode === "external" && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label htmlFor="appcfg-bundle-id" className="text-sm">
                Bundle ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="appcfg-bundle-id"
                value={value.bundleId}
                onChange={(e) =>
                  onChange({ ...value, bundleId: e.target.value })
                }
                placeholder="com.example.app"
                className="mt-1 font-mono text-sm"
                aria-invalid={!!errors?.bundleId}
                disabled={locked}
              />
              {errors?.bundleId && (
                <p className="text-xs text-destructive mt-1">
                  {errors.bundleId}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="appcfg-app-name" className="text-sm">
                App name{" "}
                <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="appcfg-app-name"
                value={value.appName}
                onChange={(e) =>
                  onChange({ ...value, appName: e.target.value })
                }
                placeholder="Display name shown in the list"
                className="mt-1"
                maxLength={80}
                aria-invalid={!!errors?.appName}
                disabled={locked}
              />
              {errors?.appName && (
                <p className="text-xs text-destructive mt-1">
                  {errors.appName}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export const validateAppConfigTarget = (
  value: AppConfigTargetValue,
): AppConfigTargetErrors => {
  const errors: AppConfigTargetErrors = {};

  if (value.mode === "catalog") {
    if (!value.catalogAppId) {
      errors.catalogAppId = WIZARD_VALIDATION_MESSAGES.catalogAppRequired;
    }
    if (!value.bundleId) {
      errors.bundleId = WIZARD_VALIDATION_MESSAGES.bundleIdMissingFromCatalog;
    }
    return errors;
  }

  // External — bundle ID is required, app name is optional but length-bounded
  // when present (matches PPPC's PppcTargetStep contract).
  const bundleErr = validateBundleId(value.bundleId);
  if (bundleErr) errors.bundleId = bundleErr;

  if (value.appName.trim().length > 0) {
    const nameErr = validateAppName(value.appName);
    if (nameErr) errors.appName = nameErr;
  }

  return errors;
};
